import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {Picker} from '@react-native-picker/picker';
import {launchCamera, launchImageLibrary} from 'react-native-image-picker';
import {ShowToast} from '../Common/common';
import {
  TasmacScanLine,
  TasmacScanResult,
  useLazyGetTasmacScanQuery,
  useScanTasmacInvoiceMutation,
} from '../redux/services/stepurchaseApi';

import T from '../Components/T';
import { tp } from '../utils/translateText';
// Scan a TASMAC liquor invoice photo, review what was read, and hand the confirmed lines to the Purchase screen.
// Nothing is saved here; the Purchase screen's Save does that.

export interface TasmacApplyLine {
  productId: number;
  name: string;
  qty: number;
  rate: number;
  amount: number;
}

export interface TasmacApplyPayload {
  result: TasmacScanResult;
  supplierId: number | null;
  storeId: number;
  lines: TasmacApplyLine[];
}

interface Props {
  visible: boolean;
  onClose: () => void;
  suppliers: {id: number; label: string}[];
  products: {pr_id: number; pr_name: string}[];
  stores: {sm_id: number; sm_name: string}[];
  defaultStoreId: number;
  onApply: (p: TasmacApplyPayload) => void;
}

const POLL_MS = 3000;
const MAX_POLLS = 90;
const money = (n: number) => Number(n).toFixed(2);

const TasmacScanModal = ({visible, onClose, suppliers, products, stores, defaultStoreId, onApply}: Props) => {
  const cancelled = useRef(false);
  const [scan] = useScanTasmacInvoiceMutation();
  const [fetchScan] = useLazyGetTasmacScanQuery();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<TasmacScanResult | null>(null);
  const [supplierId, setSupplierId] = useState<number>(0);
  const [storeId, setStoreId] = useState<number>(defaultStoreId);
  const [picks, setPicks] = useState<number[]>([]);
  const [include, setInclude] = useState<boolean[]>([]);
  // Line whose item is being searched for (null = search closed).
  const [searchLine, setSearchLine] = useState<number | null>(null);
  const [query, setQuery] = useState('');

  const mlSet = useMemo(() => new Set(result?.mlProductIds || []), [result]);
  const nameOf = (id: number) => products.find(p => p.pr_id === id)?.pr_name || '';

  useEffect(() => {
    cancelled.current = !visible;
    if (visible) {
      setBusy(false);
      setError('');
      setResult(null);
      setStoreId(defaultStoreId);
    }
  }, [visible, defaultStoreId]);

  const pick = async (source: 'camera' | 'library') => {
    // The picker re-encodes to JPEG and downsizes, so no extra image library is needed.
    const options = {mediaType: 'photo' as const, cameraType: 'back' as const, quality: 0.8 as const, maxWidth: 2000, maxHeight: 2000};
    try {
      const r = source === 'camera' ? await launchCamera(options) : await launchImageLibrary(options);
      const asset = r.assets?.[0];
      if (r.didCancel || !asset?.uri) return;
      await upload({uri: asset.uri, type: asset.type || 'image/jpeg', name: asset.fileName || 'invoice.jpg'});
    } catch (e) {
      setError('I could not open the camera or gallery.');
    }
  };

  const upload = async (file: {uri: string; type: string; name: string}) => {
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('image', file as any);
      const {jobId} = await scan(form).unwrap();
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_MS));
        if (cancelled.current) return;
        const r = await fetchScan(jobId).unwrap();
        if (r.status === 'done') {
          setResult(r);
          setSupplierId(r.supplier?.id ?? 0);
          setPicks((r.lines || []).map(l => l.productId || 0));
          setInclude((r.lines || []).map(() => true));
          return;
        }
      }
      setError('Reading the invoice is taking longer than usual. Please try again in a minute.');
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Could not read the invoice.');
    } finally {
      setBusy(false);
    }
  };

  // Quantity/rate for a line given the item picked for it (ml-stocked items are bought in ml).
  const stockFor = (l: TasmacScanLine, productId: number) => {
    const ml = mlSet.has(productId) && l.sizeMl > 0;
    const qty = ml ? l.bottles * l.sizeMl : l.bottles;
    return {qty, rate: Math.round((l.amount / qty) * 10000) / 10000, ml};
  };

  const lines = result?.lines || [];
  const missing = lines.some((_, i) => include[i] && !picks[i]);

  const apply = () => {
    if (!result) return;
    if (!storeId) {
      ShowToast('Select the store', 'error');
      return;
    }
    const chosen: TasmacApplyLine[] = [];
    lines.forEach((l, i) => {
      const productId = picks[i];
      if (!include[i] || !productId) return;
      const s = stockFor(l, productId);
      chosen.push({productId, name: nameOf(productId) || l.desc, qty: s.qty, rate: s.rate, amount: l.amount});
    });
    if (!chosen.length) {
      ShowToast('Select at least one line', 'error');
      return;
    }
    onApply({result, supplierId: supplierId || null, storeId, lines: chosen});
  };

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? products.filter(p => p.pr_name.toLowerCase().includes(q)) : products;
    return list.slice(0, 60);
  }, [products, query]);

  const btn = (title: string, onPress: () => void, color = '#146C94', disabled = false) => (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={{backgroundColor: disabled ? '#9ca3af' : color, padding: 12, borderRadius: 8, marginVertical: 4, alignItems: 'center'}}>
      <Text style={{color: '#fff', fontWeight: '600'}}>{title}</Text>
    </TouchableOpacity>
  );

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={busy ? undefined : onClose}>
      <View style={{flex: 1, backgroundColor: '#fff', padding: 12}}>
        <Text style={{fontSize: 18, fontWeight: '700', marginBottom: 8}}><T>Scan TASMAC Invoice</T></Text>

        {!result && (
          <View style={{flex: 1, justifyContent: 'center'}}>
            {busy ? (
              <View style={{alignItems: 'center'}}>
                <ActivityIndicator size="large" />
                <Text style={{marginTop: 12, textAlign: 'center'}}><T>Reading the invoice. This can take a minute or two…</T></Text>
              </View>
            ) : (
              <>
                <Text style={{textAlign: 'center', marginBottom: 12, color: '#555'}}>
                  <T>Take a clear, flat photo of the whole TASMAC invoice. You can review everything before it goes into the purchase.</T>
                </Text>
                {btn('Take Photo', () => pick('camera'))}
                {btn('Choose from Gallery', () => pick('library'), '#19A7CE')}
              </>
            )}
            {!!error && <Text style={{color: '#b91c1c', textAlign: 'center', marginTop: 12}}>{error}</Text>}
          </View>
        )}

        {result && (
          <ScrollView keyboardShouldPersistTaps="handled">
            {(result.warnings || []).map(w => (
              <Text key={w} style={{backgroundColor: '#fef3c7', padding: 8, marginBottom: 6, borderRadius: 6}}>{w}</Text>
            ))}

            <Text style={{fontWeight: '600'}}><T>Supplier (read)</T>: {result.supplier?.name || '-'}</Text>
            <View style={{borderWidth: 1, borderColor: supplierId ? '#ccc' : '#dc2626', borderRadius: 6, marginBottom: 8}}>
              <Picker selectedValue={supplierId} onValueChange={v => setSupplierId(Number(v))}>
                <Picker.Item label="Select supplier" value={0} />
                {suppliers.map(s => (
                  <Picker.Item key={s.id} label={s.label} value={s.id} />
                ))}
              </Picker>
            </View>

            <Text style={{fontWeight: '600'}}><T>Store</T></Text>
            <View style={{borderWidth: 1, borderColor: '#ccc', borderRadius: 6, marginBottom: 8}}>
              <Picker selectedValue={storeId} onValueChange={v => setStoreId(Number(v))}>
                <Picker.Item label="Select store" value={0} />
                {stores.map(s => (
                  <Picker.Item key={s.sm_id} label={s.sm_name} value={Number(s.sm_id)} />
                ))}
              </Picker>
            </View>

            <Text style={{marginBottom: 8}}>
              <T>Invoice</T> <Text style={{fontWeight: '700'}}>{result.invoice?.no || '-'}</Text> · {result.invoice?.date || '-'} <T>· Total</T>{' '}
              <Text style={{fontWeight: '700'}}>{result.invoice?.total != null ? money(result.invoice.total) : '-'}</Text>
            </Text>

            {lines.map((l, i) => {
              const pid = picks[i];
              const s = pid ? stockFor(l, pid) : null;
              const optIds = l.options.map(o => o.id);
              const shown = pid && !optIds.includes(pid) ? [...optIds, pid] : optIds;
              return (
                <View
                  key={i}
                  style={{borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, padding: 8, marginBottom: 8, opacity: include[i] ? 1 : 0.45}}>
                  <TouchableOpacity onPress={() => setInclude(include.map((x, k) => (k === i ? !x : x)))}>
                    <Text style={{fontWeight: '600'}}>
                      {include[i] ? '☑' : '☐'} {l.desc} {l.sizeMl}ml × {l.bottles}
                      {l.suspect ? '  ⚠ check' : ''}
                    </Text>
                  </TouchableOpacity>
                  <Text style={{color: '#666', fontSize: 12}}><T>Brand no.</T> {l.brandNo || '-'} · <T>Rs</T> {money(l.amount)}</Text>
                  <View style={{borderWidth: 1, borderColor: include[i] && !pid ? '#dc2626' : '#ccc', borderRadius: 6, marginTop: 4}}>
                    <Picker selectedValue={pid || 0} onValueChange={v => setPicks(picks.map((x, k) => (k === i ? Number(v) : x)))}>
                      <Picker.Item label="Select item" value={0} />
                      {shown.map(id => (
                        <Picker.Item key={id} label={nameOf(id) || l.options.find(o => o.id === id)?.name || String(id)} value={id} />
                      ))}
                    </Picker>
                  </View>
                  <TouchableOpacity onPress={() => { setQuery(''); setSearchLine(i); }}>
                    <Text style={{color: '#146C94', marginTop: 4}}><T>Search all items…</T></Text>
                  </TouchableOpacity>
                  <Text style={{marginTop: 2}}>
                    <T>Stock qty:</T> {s ? `${s.qty.toLocaleString('en-IN')} ${s.ml ? 'ml' : 'btl'}` : '-'}
                  </Text>
                </View>
              );
            })}

            <Text style={{textAlign: 'right', marginVertical: 4}}>
              <T>TCS</T> {money(result.charges?.tcs || 0)} · <T>Other</T> {money(result.charges?.otherCharges || 0)} · <T>Round off</T>{' '}
              {money(result.charges?.roundOff || 0)}
            </Text>
            {btn('Use in Purchase', apply, '#16a34a', missing)}
            {btn('Scan Another', () => setResult(null), '#6b7280')}
          </ScrollView>
        )}

        {!busy && btn('Close', onClose, '#6b7280')}

        {/* Item search for one line */}
        <Modal visible={searchLine !== null} animationType="fade" transparent onRequestClose={() => setSearchLine(null)}>
          <View style={{flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', padding: 16}}>
            <View style={{backgroundColor: '#fff', borderRadius: 10, padding: 12, maxHeight: '80%'}}>
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder={tp("Search item")}
                autoFocus
                style={{borderWidth: 1, borderColor: '#ccc', borderRadius: 6, padding: 8, marginBottom: 8}}
              />
              <FlatList
                data={searchResults}
                keyExtractor={p => String(p.pr_id)}
                keyboardShouldPersistTaps="handled"
                renderItem={({item}) => (
                  <TouchableOpacity
                    onPress={() => {
                      if (searchLine !== null) setPicks(picks.map((x, k) => (k === searchLine ? item.pr_id : x)));
                      setSearchLine(null);
                    }}
                    style={{paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#eee'}}>
                    <Text>{item.pr_name}</Text>
                  </TouchableOpacity>
                )}
              />
              {btn('Cancel', () => setSearchLine(null), '#6b7280')}
            </View>
          </View>
        </Modal>
      </View>
    </Modal>
  );
};

export default TasmacScanModal;
